import type { Language } from "../strings";
import { popularQuestions, rankedHomeQuestions, type PopularTopic } from "../chat/popular-questions";

/** Home shows 5 suggestions laid out as two rows — 3 up top, 2 nestled below. */
const HOME_LIMIT = 5;
const HOME_TOP_ROW = 3;

export function PopularQuestions(props: {
  language: Language;
  onPick: (question: string) => void;
  book?: string;
  chapter?: number;
  /** Home only: demand-ranked topics from /popular-topics. */
  topics?: readonly PopularTopic[];
}): JSX.Element | null {
  const inReader = props.book !== undefined && props.chapter !== undefined;
  const questions = inReader
    ? popularQuestions(props.language, props.book, props.chapter)
    : rankedHomeQuestions(props.language, props.topics ?? [], HOME_LIMIT);
  if (questions.length === 0) return null;
  const readerLabel = props.language === "es" ? "Preguntas sobre este capítulo" : "Questions about this chapter";
  const homeLabel = props.language === "es" ? "Preguntas populares" : "Popular questions";

  const pill = (question: string, index: number): JSX.Element => (
    <button
      key={question}
      type="button"
      className="popular-questions__pill"
      style={{ animationDelay: `${index * 60}ms` }}
      onClick={() => props.onPick(question)}
    >
      <span className="popular-questions__pill-index" aria-hidden="true">
        {String(index + 1).padStart(2, "0")}
      </span>
      <span className="popular-questions__pill-text">{question}</span>
    </button>
  );

  return (
    <section
      className={`popular-questions popular-questions--${inReader ? "reader" : "home"}`}
      aria-label={inReader ? readerLabel : homeLabel}
    >
      {inReader ? <span className="popular-questions__kicker">{readerLabel}</span> : null}
      {inReader ? (
        <div className="popular-questions__list">
          {questions.map((question, index) => pill(question, index))}
        </div>
      ) : (
        // Home: two centered rows (3 + 2). The pair below sits tight under the
        // trio — the row offset makes the zig-zag — and the cloud is free to
        // flow a touch wider than the composer.
        <div className="popular-questions__cloud">
          <div className="popular-questions__row">
            {questions.slice(0, HOME_TOP_ROW).map((question, index) => pill(question, index))}
          </div>
          {questions.length > HOME_TOP_ROW ? (
            <div className="popular-questions__row">
              {questions
                .slice(HOME_TOP_ROW)
                .map((question, index) => pill(question, index + HOME_TOP_ROW))}
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
